import { QueryClient } from '@tanstack/react-query';

export const hubQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export const hubQueryKeys = {
  vendorOverview: (vendorId: string) => ['hub', 'vendor', 'overview', vendorId] as const,
  vendorSeo: (vendorId: string) => ['hub', 'vendor', 'seo', vendorId] as const,
  vendorAppointments: (vendorId: string) => ['hub', 'vendor', vendorId, 'appointments'] as const,
  proOverview: (proId: string) => ['hub', 'pro', 'overview', proId] as const,
  adminOverview: () => ['hub', 'admin', 'overview'] as const,
  adminOverviewStats: () => ['hub', 'admin', 'overview', 'stats'] as const,
  adminPendingApprovals: () => ['hub', 'admin', 'approvals', 'pending'] as const,
  adminRecentOrders: () => ['hub', 'admin', 'orders', 'recent'] as const,
  adminAdPerformance: () => ['hub', 'admin', 'ads', 'performance'] as const,
  adminTopVendors: () => ['hub', 'admin', 'vendors', 'top'] as const,
  workspace: (userId: string, vendorId?: string | null, proId?: string | null) =>
    ['hub', 'workspace', userId, vendorId ?? null, proId ?? null] as const,
  orgMembers: (orgId: string) => ['hub', 'org', orgId, 'members'] as const,
  platformConfig: ['hub', 'platform', 'config'] as const,
  planDefinitions: ['hub', 'platform', 'plans'] as const,
  platformDocuments: () => ['hub', 'platform', 'documents'] as const,
  userConsents: (userId: string) => ['hub', 'consent', userId] as const,
  vendorProducts: (vendorId: string) => ['hub', 'vendor', vendorId, 'products'] as const,
  vendorInventoryLog: (vendorId: string) => ['hub', 'vendor', vendorId, 'inventory-log'] as const,
  vendorOrders: (vendorId: string) => ['hub', 'vendor', vendorId, 'orders'] as const,
  adminUsers: () => ['hub', 'admin', 'users'] as const,
  adminUserDetail: (userId: string) => ['hub', 'admin', 'users', userId] as const,
  adminUserAudit: (userId: string) => ['hub', 'admin', 'users', userId, 'audit'] as const,
  adminApprovals: () => ['hub', 'admin', 'approvals'] as const,
  adminProducts: () => ['hub', 'admin', 'products'] as const,
  /** Enriched vendor rows for Admin Vendors (VendorRow[]). */
  adminVendors: () => ['hub', 'admin', 'vendors', 'enriched'] as const,
  /** Plain Vendor[] directory lookup (orders, pickers). */
  adminVendorDirectory: () => ['hub', 'admin', 'vendors', 'directory'] as const,
  adminModeration: () => ['hub', 'admin', 'moderation'] as const,
  adminInbox: () => ['hub', 'admin', 'inbox'] as const,
  adminCommandPalette: () => ['hub', 'admin', 'command-palette'] as const,
  adminOrders: () => ['hub', 'admin', 'orders'] as const,
  adminReviews: () => ['hub', 'admin', 'reviews'] as const,
  adminClassifieds: () => ['hub', 'admin', 'classifieds'] as const,
  adminProperties: () => ['hub', 'admin', 'properties'] as const,
  adminServiceRequests: () => ['hub', 'admin', 'service-requests'] as const,
  adminPros: () => ['hub', 'admin', 'pros'] as const,
  adminSubscriptions: () => ['hub', 'admin', 'subscriptions'] as const,
  /** Plain ServiceRequest[] — do not reuse proRequestsWithQuotes cache shape. */
  proRequestsList: (proId: string) => ['hub', 'pro', proId, 'requests-list'] as const,
  proRequestsWithQuotes: (proId: string) => ['hub', 'pro', proId, 'requests-with-quotes'] as const,
  proServices: (providerId: string) => ['hub', 'pro', providerId, 'services'] as const,
  adminCategories: () => ['hub', 'admin', 'categories'] as const,
  adminSolarCompanies: () => ['hub', 'admin', 'seo', 'solar'] as const,
  adminParishIntros: () => ['hub', 'admin', 'seo', 'parish-intros'] as const,
  adminSlugRedirects: () => ['hub', 'admin', 'seo', 'redirects'] as const,
  adminSeoHubCounts: () => ['hub', 'admin', 'seo', 'overview-counts'] as const,
  adminSeoWorkbench: () => ['hub', 'admin', 'seo', 'workbench'] as const,
  adminFinance: () => ['hub', 'admin', 'finance'] as const,
  adminAds: () => ['hub', 'admin', 'ads'] as const,
  adminAudit: () => ['hub', 'admin', 'audit'] as const,
  adminGiftCards: () => ['hub', 'admin', 'gift-cards'] as const,
  adminReferrals: () => ['hub', 'admin', 'referrals'] as const,
  adminDocuments: () => ['hub', 'admin', 'documents'] as const,
  adminSupport: () => ['hub', 'admin', 'support'] as const,
  adminSupportThread: (threadId: string) => ['hub', 'admin', 'support', threadId] as const,
  adminMetrics: () => ['hub', 'admin', 'metrics'] as const,
  adminCommunications: () => ['hub', 'admin', 'communications'] as const,
  adminPlatformSettings: () => ['hub', 'admin', 'platform-settings'] as const,
  orgTeamServices: (orgId: string) => ['hub', 'org', orgId, 'team-services'] as const,
  vendorPromotions: (vendorId: string) => ['hub', 'vendor', vendorId, 'promotions'] as const,
  vendorFlashSales: (vendorId: string) => ['hub', 'vendor', vendorId, 'flash-sales'] as const,
  vendorBundles: (vendorId: string) => ['hub', 'vendor', vendorId, 'bundles'] as const,
  vendorPropertyInquiries: (userId: string) => ['hub', 'vendor', userId, 'property-inquiries'] as const,
  notifications: (userId: string) => ['notifications', userId] as const,
};
